import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import useDriftClient from 'src/hooks/useDriftClient';
import { WithClassName } from 'src/utils/UtilityTypes';
import { BigNum, BN, QUOTE_PRECISION_EXP, ZERO } from '@drift-labs/sdk';
import { useMemo } from 'react';
import useGetRawOracleDataForMarket from 'src/hooks/useGetRawOracleDataForMarket';
import { MarketId } from '@drift/common';
import { Edit } from '@drift-labs/icons';
import useModal from 'src/components/Modals/hooks/useModal';
import { useIsolatedPositionMargin } from 'src/hooks/positions/useIsolatedPositionMargin';
import { cm } from 'src/utils/classNames';

interface Props extends WithClassName {
	marketIndex: number;
}

export default function OpenPositionsTableMarginDisplay({
	marketIndex,
	className,
}: Props) {
	const driftClient = useDriftClient();
	const { show } = useModal();

	const user = driftClient?.hasUser() ? driftClient?.getUser() : undefined;
	const perpPosition = user?.getPerpPosition(marketIndex);
	const getRawOracleDataForMarket = useGetRawOracleDataForMarket();

	const oraclePriceData = getRawOracleDataForMarket(
		MarketId.createPerpMarket(marketIndex)
	);
	const isolatedMargin = useIsolatedPositionMargin(marketIndex);

	const userCollateral = user?.getTotalCollateral() ?? ZERO;
	const hc = useMemo(
		() =>
			perpPosition && user
				? user?.getPerpPositionHealth({
						marginCategory: 'Initial',
						includeOpenOrders: false,
						perpPosition,
						oraclePriceData,
					})
				: undefined,
		[oraclePriceData?.price?.toNumber(), user, perpPosition]
	);

	const isIsolated =
		perpPosition && user ? user?.isPerpPositionIsolated(perpPosition) : false;
	const value = BigNum.from(
		!isIsolated
			? BN.min(userCollateral, hc?.weightedValue ?? ZERO)
			: isolatedMargin.val,
		QUOTE_PRECISION_EXP
	);

	const content = useMemo(() => {
		return (
			<>
				<span className="text-text-default">
					<NumberDisplay value={value} type="currency" className={className} />
				</span>

				{isIsolated && (
					<span className="flex items-center text-text-secondary transition-all group-hover:text-text-default">
						<Edit size={18} />
					</span>
				)}
			</>
		);
	}, [value, isIsolated, className]);

	if (!user || !perpPosition || !hc) {
		return <span className={className}>-</span>;
	}

	return (
		<>
			{!isIsolated ? (
				<span className={cm(className, 'flex items-center')}>{content}</span>
			) : (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						show({ type: 'isolated_position_margin', props: { marketIndex } });
					}}
					className="group flex items-center gap-1 py-1 text-text-tertiary hover:text-text-default"
					aria-label="Edit isolated margin"
				>
					{content}
				</button>
			)}
		</>
	);
}
